import Skeleton from '../components/common/Skeleton';
import LogJobHeader from './LogJobHeader';
import { styles } from './logJobStyles';
import { layoutStyles as L } from './logJobLayoutStyles';
import { sectionStyles } from '../components/jobs/jobSectionStyles';
import { barStyles } from './logJobBarStyles';
import RailFrame from '../components/common/fullscreenFlow/RailFrame';

// One-shot reveal fade (mirrors Skeleton.js's lazy keyframe injection). The
// populated form mounts with this animation so the skeleton resolves into
// content with a single soft fade instead of a hard cut.
let revealInjected = false;
function injectRevealKeyframes() {
  if (revealInjected || typeof document === 'undefined') return;
  revealInjected = true;
  const s = document.createElement('style');
  s.textContent =
    '@keyframes logjobReveal{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}';
  document.head.appendChild(s);
}

// Applied by LogJob's form root once `revealReady` flips true.
export const LOGJOB_REVEAL_ANIM = 'logjobReveal 200ms ease-out';

// Twin of JobSection.js's resting frame (2026-09-11): a header placeholder
// ON THE CANVAS (name + one right-aligned figure, no background/border) sits
// above a separate white card holding the line placeholders — never a single
// boxed block with its title inside, which was the old duplicate-label shape.
// Pulls `root`/`header`/`card` straight from `sectionStyles(layout)` so the
// skeleton's geometry can never drift from the real section shell; only the
// two Skeleton bar heights are literal (18 phone / 20 tablet+desktop, per
// spec) since the real header sizes its text by font, not by a fixed box.
function SkeletonSection({ layout, lines }) {
  const s = sectionStyles(layout);
  const barHeight = layout === 'phone' ? 18 : 20;
  return (
    <div style={s.root}>
      <div style={s.header}>
        <Skeleton width="40%" height={barHeight} borderRadius={6} />
        <Skeleton width={44} height={barHeight} borderRadius={6} style={{ marginLeft: 'auto', flexShrink: 0 }} />
      </div>
      {/* The card itself is padding-free (its real children are full-bleed
          rows), so the placeholders carry `cardInset` exactly as the real
          non-row content does. */}
      <div style={s.card} data-testid="logjob-skeleton-section">
        <div style={s.cardInset} data-testid="logjob-skeleton-inset">
          {Array.from({ length: lines }).map((_, i) => (
            <Skeleton key={i} width="100%" height={12} borderRadius={6} style={i === lines - 1 ? undefined : { marginBottom: 8 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function HeroPlaceholder({ isMobile }) {
  return (
    <div style={{ ...styles.heroCard, ...(isMobile ? styles.heroCardMobile : {}) }}>
      <Skeleton width={140} height={20} borderRadius={6} style={{ marginBottom: 10 }} />
      <Skeleton width={184} height={15} borderRadius={6} style={{ marginBottom: 8 }} />
      <Skeleton width={220} height={12} borderRadius={6} style={{ marginBottom: 14 }} />
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Skeleton width={52} height={10} borderRadius={999} />
        <Skeleton width={20} height={20} borderRadius={9999} />
        <Skeleton width={84} height={10} borderRadius={999} />
      </div>
    </div>
  );
}

/**
 * Full-layout loading shell for LogJob, per Direction 1 frame. Paints the REAL
 * header (compact on phone, breadcrumb elsewhere) so the header never changes
 * on reveal, then placeholders in the exact frame of the populated form.
 * `headerNode` (KAN-765) swaps in the unscheduled-visit flow's header when Log
 * Job is that flow's step 3, so the header is identical before and after reveal.
 * `bare` renders ONLY what sits under the header (no container, no header): Log
 * Job hosts it inside the unscheduled-visit flow's moving body wrapper, which
 * stays mounted when the form replaces this skeleton (the push is never cut).
 */
export default function LogJobSkeleton({
  layout = 'desktop', customerName = '', isMobile, onBack = () => {}, headerNode = null, bare = false,
  // Unscheduled-visit desktop arrival (KAN-771): the REAL identity card (known
  // from the flow's router state) in place of the placeholder, the form pane's
  // own push style, and the rail raised above that pane.
  identityNode = null, formPaneStyle = null, railStyle = null,
}) {
  injectRevealKeyframes();
  const mode = isMobile ? 'phone' : layout;
  const phone = mode === 'phone';
  const body = (
    <>
      {phone ? (
        <div style={L.contextStrip}><Skeleton width={160} height={11} borderRadius={999} /><Skeleton width={110} height={18} borderRadius={999} /></div>
      ) : null}
      {mode === 'tablet' ? <HeroPlaceholder isMobile={false} /> : null}
      <SkeletonSection layout={mode} lines={1} />
      <SkeletonSection layout={mode} lines={2} />
      <SkeletonSection layout={mode} lines={1} />
    </>
  );

  const underHeader = (
    <>
      {phone ? (
        <div style={L.jumpStrip} data-testid="logjob-skeleton-jump">
          {[64, 60, 52, 44].map((w, i) => <Skeleton key={i} width={w} height={32} borderRadius={999} />)}
        </div>
      ) : null}

      {mode === 'desktop' ? (
        <div style={L.twoPane}>
          <div style={formPaneStyle ? { ...L.formPane, ...formPaneStyle } : L.formPane}><div style={{ ...L.column, ...L.columnDesktop, ...L.columnPadDesktop }} data-testid="logjob-skeleton-column">{body}</div></div>
          {/* Rail order mirrors LogJobSummaryRail.js: identity -> breakdown
              card -> last-visit card -> submit footer. */}
          <RailFrame
            style={railStyle}
            testId="logjob-skeleton-rail"
            footer={<div style={L.railSubmitBlock}><Skeleton width="100%" height={52} borderRadius={12} /></div>}
          >
            {identityNode || <HeroPlaceholder isMobile={false} />}
            {/* The real rail overrides its LAST breakdown row to
                `borderBottom:'none'` (LogJobSummaryRail.js), so the skeleton
                must too — otherwise a hairline disappears on reveal. */}
            <div style={L.railCard}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={i === 3 ? { ...L.railRow, borderBottom: 'none' } : L.railRow}>
                  <Skeleton width={70} height={10} borderRadius={999} />
                  <Skeleton width={54} height={10} borderRadius={999} />
                </div>
              ))}
            </div>
            {/* No last-visit placeholder: the real card renders only when the
                customer HAS a previous visit, and the skeleton cannot know.
                Painting it unconditionally guaranteed a shape change on reveal
                for every first-time customer; omitting it is wrong only for
                returning ones, where the card simply fades in. */}
          </RailFrame>
        </div>
      ) : (
        <div style={{ ...styles.scrollWrap, ...(phone ? styles.scrollWrapMobile : {}) }}>
          <div style={mode === 'tablet' ? { ...L.column, ...L.columnPadTablet } : { ...styles.content, ...styles.contentMobile }} data-testid="logjob-skeleton-column">{body}</div>
        </div>
      )}

      {/* Twin of LogJobSubmitBar's `bar` variant (KAN-1078): ONE row - the stacked
          total on the left, the natural-width primary on the right. */}
      {mode !== 'desktop' ? (
        <div style={{ ...styles.bottomBar, ...(phone ? styles.bottomBarMobile : {}) }}>
          <div style={{ ...L.barInner, ...(phone ? L.barInnerFull : {}), flexDirection: 'row', gap: 0 }}>
            <div style={{ ...barStyles.leftTotal, gap: 6 }}>
              <Skeleton width={96} height={12} borderRadius={999} />
              <Skeleton width={84} height={22} borderRadius={6} />
            </div>
            <Skeleton width={140} height={44} borderRadius={12} />
          </div>
        </div>
      ) : null}
    </>
  );

  if (bare) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} aria-busy="true" data-testid="logjob-skeleton">
        {underHeader}
      </div>
    );
  }

  return (
    <div style={styles.container} aria-busy="true" data-testid="logjob-skeleton">
      {headerNode || <LogJobHeader layout={mode} customerName={customerName} dateLabel="" onBack={onBack} />}
      {underHeader}
    </div>
  );
}
